// Order message-log entries to follow the sync pipeline's processing steps:
// group by lambda/step in the order each first touched the record (oxi-enqueue →
// opera-oxi → oracle-ohip → …), and within each step show ENTRY before EXIT.
//
// The step order is derived from each lambda's earliest ENTRY timestamp, so it tracks
// whatever connector chain the sync template defines without hardcoding names. Using the
// ENTRY (not any entry) is deliberate: a record reprocessed across days keeps STALE exits
// from earlier runs, and an old exit's timestamp would otherwise drag its lambda to the top
// (e.g. an oracle-ohip EXIT from yesterday ranking oracle-ohip before today's oxi-enqueue).
// A lambda with only exits (no entry in the log) falls back to its earliest timestamp.
// The sort is deterministic (stable for a given input), which matters because the
// render keys per-entry UI state (decoded payloads, expand/collapse) by array index.

const DIRECTION_RANK = { entry: 0, error: 1, exit: 2 };

function lambdaKey(entry) {
    return entry.lambda || entry.lambdaName || '-';
}

function entryTime(entry, fallback) {
    const t = entry.timestamp ? new Date(entry.timestamp).getTime() : NaN;
    return Number.isNaN(t) ? fallback : t;
}

export default function orderMessageLog(entries) {
    if (!Array.isArray(entries) || entries.length === 0) return [];

    // Step order = each lambda's earliest ENTRY timestamp (ignores stale exits from prior runs).
    const firstSeen = {};
    entries.forEach((e, idx) => {
        if (String(e.direction || '').toLowerCase() !== 'entry') return;
        const key = lambdaKey(e);
        const t = entryTime(e, idx);
        if (firstSeen[key] === undefined || t < firstSeen[key]) firstSeen[key] = t;
    });
    // Fallback for a lambda that has no ENTRY in the log (only exits): use its earliest timestamp.
    entries.forEach((e, idx) => {
        const key = lambdaKey(e);
        if (firstSeen[key] !== undefined) return;
        const t = entryTime(e, idx);
        if (firstSeen[key] === undefined || t < firstSeen[key]) firstSeen[key] = t;
    });

    return entries
        .map((e, idx) => ({ e, idx }))
        .sort((a, b) => {
            // 1) pipeline step (by lambda's first appearance)
            const fa = firstSeen[lambdaKey(a.e)];
            const fb = firstSeen[lambdaKey(b.e)];
            if (fa !== fb) return fa - fb;
            // 2) within a step: ENTRY, then any error, then EXIT
            const da = DIRECTION_RANK[String(a.e.direction || '').toLowerCase()] ?? 1;
            const db = DIRECTION_RANK[String(b.e.direction || '').toLowerCase()] ?? 1;
            if (da !== db) return da - db;
            // 3) tie-break by time, then original position (keeps the sort stable)
            const ta = entryTime(a.e, a.idx);
            const tb = entryTime(b.e, b.idx);
            if (ta !== tb) return ta - tb;
            return a.idx - b.idx;
        })
        .map(x => x.e);
}
